import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { CurrencyInput, DocumentNumberField, FormField, SearchField } from '@/components/custom/erp';
import { MA_CHUNG_TU, QUERY_KEYS, warehouseSearchColumns } from '@/constants';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

type GiaoDichOption = {
  uuid: string;
  ten_gd: string;
};

interface BasicInfoTabProps {
  formMode: FormMode;
  // Props for search field states
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

export const BasicInfoTab = ({ formMode, formState: { state, actions } }: BasicInfoTabProps) => {
  const isDisabled = formMode === 'view';
  const { data } = useCRUD<GiaoDichOption>({
    endpoint: `${QUERY_KEYS.MA_GIAO_DICH}?ma_ct=${MA_CHUNG_TU.KHO.PHIEU_XUAT_DIEU_CHUYEN}`
  });
  const transactionOptions = data.map(item => ({ value: item.uuid, label: item.ten_gd }));
  const { watch, setValue } = useFormContext();
  const transactionCode = watch('ma_gd');
  const defaultTransactionValue = transactionOptions[0]?.value;

  useEffect(() => {
    if (!transactionCode && defaultTransactionValue) {
      setValue('ma_gd', defaultTransactionValue);
    }
  }, [defaultTransactionValue, setValue, transactionCode]);

  return (
    <div className='space-y-2 p-4'>
      <div className='flex flex-col gap-6 lg:flex-row lg:gap-8'>
        <div className='flex-1 space-y-2'>
          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Giao dịch</Label>
            <div className='w-[260px]'>
              <FormField name='ma_gd' type='select' disabled={isDisabled} options={transactionOptions} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Mã kho xuất</Label>
            <SearchField
              name='ma_kho'
              searchEndpoint={`/${QUERY_KEYS.KHO_HANG}`}
              searchColumns={warehouseSearchColumns}
              dialogTitle='Chọn kho nhập'
              columnDisplay='ma_kho'
              displayRelatedField='ten_kho'
              value={state.selectedWarehouse?.ma_kho || ''}
              relatedFieldValue={state.selectedWarehouse?.ten_kho || ''}
              onRowSelection={actions.setSelectedWarehouse}
              disabled={isDisabled}
            />
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Diễn giải</Label>
            <div className='flex-1'>
              <FormField name='dien_giai' type='text' disabled={isDisabled} />
            </div>
          </div>
        </div>

        <div className='w-full space-y-3 lg:w-[25rem]'>
          <DocumentNumberField
            ma_ct={MA_CHUNG_TU.TON_KHO.PHIEU_NHAP_DIEU_CHUYEN}
            quyenChungTu={state.selectedDocument}
            onQuyenChungTuChange={actions.setSelectedDocument}
            soChungTu={state.selectedDocumentNumber}
            onSoChungTuChange={actions.setSelectedDocumentNumber}
            disabled={formMode === 'view'}
            classNameSearchField='w-full'
            formMode={formMode}
          />

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Ngày chứng từ</Label>
            <div className='flex-1'>
              <FormField name='ngay_ct' type='date' disabled={isDisabled} />
            </div>
          </div>

          <CurrencyInput formMode={formMode} />

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Trạng thái</Label>
            <div className='flex-1'>
              <FormField
                name='status'
                type='select'
                disabled={isDisabled}
                options={[
                  { value: '1', label: 'Lập chứng từ' },
                  { value: '3', label: 'Chờ duyệt' },
                  { value: '5', label: 'Nhập kho' }
                ]}
              />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <FormField label='Dữ liệu được nhận' name='transfer_yn' type='checkbox' disabled={isDisabled} />
          </div>
        </div>
      </div>
    </div>
  );
};
